import React, { useState, useCallback, useRef } from "react";
import Toggle from "../../../../Forms/Toggle";
import TextField from "../../../../Forms/TextField";
import { DatePicker } from "../../../../Forms/DatePicker";
import ChoiceGroup from "../../../../Forms/ChoiceGroup";
import TextCKEditor from "../../../../Forms/TextCKEditor";
import { useFormikContext } from "formik";
import { axiosInstance } from "../../../../../api/config";
import { Label, Text } from "@fluentui/react";

const completionRules = [
  { key: "All", text: "All action items must be completed" },
  {
    key: "Set Amount",
    text: "Only a set amount of action items need to be completed",
  },
  {
    key: "Required Only",
    text: "Only the action items that are required need to be completed",
  },
];

const orderOptions = [
  {
    key: "Ordered",
    text: "Action items need to be completed in sequential order",
  },
  { key: "Unordered", text: "Action items can be completed in any order" },
];

export const statusOptions = [
  { key: "Draft", text: "Draft" },
  { key: "Published", text: "Publish" },
];

function formatStringAsSlug(string) {
  return string && string.toLowerCase().split(" ").join("-");
}

const BasicInfoFields = ({ isPublished, checklistId, onSlugTakenChange }) => {
  const { setFieldValue, getFieldProps, getFieldMeta } = useFormikContext();
  const [slugError, setSlugError] = useState("");
  const debounceRef = useRef(null);

  const checkSlugAvailability = useCallback(
    (slug) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!slug) {
        setSlugError("");
        onSlugTakenChange?.(false);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        try {
          const params = { slug };
          if (checklistId) params.excludeChecklistId = checklistId;
          const { data: isAvailable } = await axiosInstance.get(
            "checklist/slug-available",
            { params }
          );
          if (!isAvailable) {
            setSlugError("This slug is already taken");
            onSlugTakenChange?.(true);
          } else {
            setSlugError("");
            onSlugTakenChange?.(false);
          }
        } catch {
          setSlugError("");
          onSlugTakenChange?.(false);
        }
      }, 400);
    },
    [checklistId, onSlugTakenChange]
  );

  const handleNameBlur = () => {
    if (!getFieldMeta("slug").initialValue) {
      const slug = formatStringAsSlug(getFieldProps("name").value);
      checkSlugAvailability(slug);
    }
  };

  const handleSlugBlur = () => {
    checkSlugAvailability(getFieldProps("slug").value);
  };

  return (
    <>
      <ChoiceGroup
        label={isPublished ? "Updated Status" : "Initial Status"}
        placeholder="Select an option"
        options={statusOptions}
        name="status"
        required
        disabled={isPublished}
      />


      {getFieldProps("status").value === statusOptions[1].key && (
        <DatePicker
          label="Published Date & Time"
          name="publishedDate"
          textField={{
            description: "The date that the checklist will be accessible",
          }}
          disabled={isPublished}
        />
      )}
      <TextField
        label="User Facing Label"
        name="label"
        onChange={(e, newValue) => {
          setFieldValue("label", newValue);
        }}
      />
      <TextField
        label="Internal Checklist Display Name"
        name="name"
        onChange={(e, newValue) => {
          setFieldValue("name", newValue);
          if (!getFieldMeta("slug").initialValue) {
            setFieldValue("slug", formatStringAsSlug(newValue));
          }
        }}
        onBlur={handleNameBlur}
      />
      <TextField
        label="Slug"
        name="slug"
        description="The url that will be used to visit the checklist"
        disabled={isPublished}
        onBlur={handleSlugBlur}
      />
      {slugError && (
        <span style={{ color: "rgb(164, 38, 44)", fontSize: 14, fontWeight: 600, paddingTop: 0, marginTop: 0, display: "block" }}>
          {slugError}
        </span>
      )}
      <TextField label="User Facing Checklist Header" name="header" required />
      <div>
        <Label>User Facing Checklist Subheader <Text variant="small" styles={{ root: { color: "#605e5c", fontWeight: 400, display: "inline" } }}>(supports HTML formatting)</Text></Label>
        <TextCKEditor
          value={getFieldProps("subheader").value || ""}
          onChange={(val) => setFieldValue("subheader", val)}
        />
      </div>
      <ChoiceGroup
        label="Action Items Completion Order"
        placeholder="Select an option"
        options={orderOptions}
        name="actionOrder"
        required
        disabled={isPublished}
      />
      <ChoiceGroup
        label="Completion Rules"
        placeholder="Select an option"
        options={completionRules}
        name="completionRules"
        required
        disabled={isPublished}
      />
       <Toggle
        name="completionCert"
        label="Certificate"
        onText="Certificate on"
        offText="Certificate off"
      />
      {getFieldProps("completionRules").value === completionRules[1].key && (
        <TextField
          label="Number of Action Items to be Completed"
          name="completeAny"
          type="number"
          description="This will determine how many action items need to be completed to complete the checklist"
          required
          disabled={isPublished}
        />
      )}
      <div>
        <Label>Completion Message <Text variant="small" styles={{ root: { color: "#605e5c", fontWeight: 400, display: "inline" } }}>(supports HTML formatting)</Text></Label>
        <Text variant="xSmall" styles={{ root: { color: "#605e5c", display: "block", marginBottom: 4 } }}>
          This message will be displayed upon completing the checklist
        </Text>
        <TextCKEditor
          value={getFieldProps("completionMessage").value || ""}
          onChange={(val) => setFieldValue("completionMessage", val)}
        />
      </div>
    </>
  );
};

export const initialValues = {
  label:"",
  name: "",
  header: "",
  subheader: "",
  actionOrder: "",
  completionRules: "",
  completeAny: 0,
  completionMessage: "",
  status: "",
  slug: "",
  publishedDate: new Date(new Date().setHours(0, 0, 0, 0)),
};
export default BasicInfoFields;
